import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: string;
  title: string;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  toast: (title: string, message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [isRTL, setIsRTL] = useState(document.documentElement.dir === 'rtl');

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsRTL(document.documentElement.dir === 'rtl');
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['dir'] });
    return () => observer.disconnect();
  }, []);

  const toast = useCallback((title: string, message: string, type: ToastType = 'info') => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { id, title, message, type }]);

    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 5000);
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="toast-stack" style={{ position: 'fixed', bottom: '24px', [isRTL ? 'left' : 'right']: '24px', zIndex: 3000, display: 'grid', gap: '8px' }}>
        {toasts.map((t) => (
          <div key={t.id} className={`toast sw-glass ${t.type}`} style={{
            display: 'flex',
            gap: '12px',
            padding: '16px 24px',
            borderRadius: '20px',
            background: 'var(--surface-glass)',
            border: '1px solid var(--border)',
            boxShadow: 'var(--shadow-lg)',
            color: 'var(--text)',
            maxWidth: '360px',
            borderLeft: isRTL ? 'none' : `4px solid var(--${t.type === 'info' ? 'brand-primary' : t.type})`,
            borderRight: isRTL ? `4px solid var(--${t.type === 'info' ? 'brand-primary' : t.type})` : 'none'
          }}>
            <div className="toast-icon" style={{
                width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: `var(--${t.type === 'info' ? 'brand-primary' : t.type})`, color: 'white', flexShrink: 0, marginTop: '2px', fontSize: '0.8rem'
            }}>
                {t.type === 'success' ? '✓' : t.type === 'error' ? '✕' : 'ℹ'}
            </div>
            <div className="toast-content">
              <strong style={{ display: 'block', fontSize: '0.95rem', marginBottom: '2px', color: 'var(--text)' }}>{t.title}</strong>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-soft)', lineHeight: 1.4 }}>{t.message}</p>
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within ToastProvider');
  return context;
};
